import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, Plus, Loader2 } from "lucide-react";
import { useLang } from "@/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const AVATAR_COLORS = ["bg-navy", "bg-turq-dark", "bg-turq", "bg-navy-light"];
const initials = (n) => n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3 focus-visible:ring-2 focus-visible:ring-turq outline-none text-navy h-auto";

function ReviewForm({ onDone }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", rating: 5, message: "", location: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error("Please fill name and review."); return; }
    setLoading(true);
    try {
      await api.post("/testimonials", form);
      toast.success("Thank you! Your review will appear after approval.");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 mt-2">
      <div><Label className="text-navy text-sm mb-1.5 block">Name</Label>
        <Input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" /></div>
      <div><Label className="text-navy text-sm mb-1.5 block">Location</Label>
        <Input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Phillaur" data-testid="review-location-input" /></div>
      <div><Label className="text-navy text-sm mb-1.5 block">Rating</Label>
        <div className="flex gap-1" data-testid="review-rating">
          {[1, 2, 3, 4, 5].map((r) => (
            <button type="button" key={r} onClick={() => setForm({ ...form, rating: r })}>
              <Star className={`w-7 h-7 ${r <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
            </button>
          ))}
        </div></div>
      <div><Label className="text-navy text-sm mb-1.5 block">Your Review</Label>
        <Textarea className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="review-message-input" /></div>
      <Button type="submit" disabled={loading} className="w-full rounded-full bg-turq hover:bg-turq-dark py-6" data-testid="review-submit-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("submit")}
      </Button>
    </form>
  );
}

export default function Testimonials() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <section id="reviews" className="py-20 md:py-28" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-turq-dark">{t("reviews_label")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-3 tracking-tight">{t("reviews_title")}</h2>
            <div className="flex items-center gap-2 mt-4">
              <div className="flex text-amber-400">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}</div>
              <span className="text-navy font-semibold">5.0</span><span className="text-secondary text-sm">• Rated by our patients</span>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-navy hover:bg-navy-light px-6 self-start" data-testid="open-review-btn"><Plus className="w-4 h-4 mr-1" /> {t("share_review")}</Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl sm:max-w-[440px]">
              <DialogHeader><DialogTitle className="font-heading text-navy">{t("share_review")}</DialogTitle></DialogHeader>
              <ReviewForm onDone={() => { setOpen(false); }} />
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {items.map((rev, i) => (
            <motion.div key={rev.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.08, 0.4) }}
              className="bg-white rounded-2xl p-7 border border-slate-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.06)]" data-testid={`testimonial-${i}`}>
              <Quote className="w-8 h-8 text-turq/30" />
              <div className="flex text-amber-400 mt-3">{[...Array(rev.rating || 5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}</div>
              <p className="text-secondary mt-3 leading-relaxed">{rev.message}</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-100">
                <div className={`w-11 h-11 rounded-full ${AVATAR_COLORS[i % AVATAR_COLORS.length]} flex items-center justify-center text-white font-semibold text-sm`}>{initials(rev.name)}</div>
                <div><p className="font-heading font-semibold text-navy text-sm">{rev.name}</p>{rev.location && <p className="text-xs text-muted">{rev.location}</p>}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
